import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput, { getSqrtInputNumericValue } from "../../../../components/inputs/SqrtInput";

export const id = "ConvertToCanonicalTask";
export const displayName = "Przekształć do postaci kanonicznej";

function formatFraction(n) {
  if (Number.isInteger(n)) return n.toString();
  const frac = formatMixedFractionLatex(n);
  return frac.includes("\\dfrac") ? frac : n.toFixed(2);
}

function formatSigned(n) {
  if (n === 0) return "0";
  const abs = Math.abs(n);
  const formatted = formatFraction(abs);
  return n < 0 ? `- ${formatted}` : `+ ${formatted}`;
}

export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRandomNonZeroEven(min, max) {
  let value = 0;
  while (value === 0 || value % 2 !== 0) value = getRandomInt(min, max);
  return value;
}

export function formatShiftedLatex(variable, value) {
  const sign = value >= 0 ? "-" : "+"; // zmieniony znak!
  const formatted = formatMixedFractionLatex(Math.abs(value));
  return `(${variable} ${sign} ${formatted})`;
}

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  const maxDen = 10;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

function generateDopełnianieKwadratu(coef) {
  const a = coef;
  const a2 = a / 2;
  const completeSquare = `${a2 < 0 ? "(x + " + Math.abs(a2) : "(x - " + a2})^2`;
  return {
    wyrazenie: `x^2 ${a < 0 ? "- " + Math.abs(a) : "+ " + a}x`,
    przekształcone: `${completeSquare}`,
    dodane: `${a2 ** 2}`,
    shift: a2
  };
}

export function generateTask() {
  let Dx = 0, Ey = 0, F = 0, a = 0, b = 0, rSquared = -1;

  while (rSquared <= 0 || !Number.isInteger(rSquared)) {
    Dx = getRandomNonZeroEven(-8, 8);
    Ey = getRandomNonZeroEven(-8, 8);

    F = getRandomInt(-10, 10);

    a = -Dx / 2;
    b = -Ey / 2;
    rSquared = a ** 2 + b ** 2 - F;
  }

  const r2Latex = formatMixedFractionLatex(rSquared);
  const left = `${formatShiftedLatex("x", a)}^2 + ${formatShiftedLatex("y", b)}^2`;
  const answerLatex = `${left} = ${r2Latex}`;

  return {
    type: "ReadCenterRadiusFromGeneralTask",
    data: {
      Dx,
      Ey,
      F,
      a,
      b,
      rSquared,
      center: { x: a, y: b },
    },
    answer: answerLatex,
  };
}

export function renderTask(task) {
  const { Dx, Ey, F } = task.data;

  const parts = [
    "x^2 + y^2",
    Dx !== 0 ? (Dx > 0 ? `+ ${Dx}x` : `- ${Math.abs(Dx)}x`) : null,
    Ey !== 0 ? (Ey > 0 ? `+ ${Ey}y` : `- ${Math.abs(Ey)}y`) : null,
    F !== 0 ? (F > 0 ? `+ ${F}` : `- ${Math.abs(F)}`) : null
  ].filter(Boolean);

  return (
    <div className="space-y-2">
      <p>Odczytaj współrzędne środka i promień okręgu na podstawie równania ogólnego:</p>
      <BlockMath math={`${parts.join(" ")} = 0`} />
    </div>
  );
}

export function normalizeRadiusAnswer(value) {
  const raw = value && typeof value === "object"
    ? (value.raw ?? value.value ?? "")
    : String(value ?? "");

  return {
    raw,
    parsed: getSqrtInputNumericValue(value),
  };
}

/** ✅ Input: 2× FractionInput dla środka + SqrtInput dla promienia */
export function renderInput(task, userInput, setUserInput, disabled) {
  const ui = userInput ?? {};

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span><InlineMath math={"x_S ="} /></span>
          <FractionInput
            value={ui?.x || ""}
            onChange={(val) => setUserInput({ ...ui, x: val })}
            disabled={disabled}
            label={"x_S"}
          />
        </div>

        <div className="flex items-center gap-2">
          <span><InlineMath math={"y_S ="} /></span>
          <FractionInput
            value={ui?.y || ""}
            onChange={(val) => setUserInput({ ...ui, y: val })}
            disabled={disabled}
            label={"y_S"}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span><InlineMath math="r =" /></span>
        <SqrtInput label={"r"}
          value={ui?.radius?.raw || ""}
          onChange={(value) => {
            setUserInput({
              ...ui,
              radius: normalizeRadiusAnswer(value),
            });
          }}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer(task, userInput) {
  const x = userInput?.x?.parsed;
  const y = userInput?.y?.parsed;
  const r = userInput?.radius?.parsed;

  const expectedX = task.data.center.x;
  const expectedY = task.data.center.y;
  const expectedR = Math.sqrt(task.data.rSquared);

  const roundTo = (v, digits = 4) =>
    Math.round(v * 10 ** digits) / 10 ** digits;

  const close = (a, b) =>
    typeof a === "number" &&
    typeof b === "number" &&
    roundTo(a) === roundTo(b);

  return close(x, expectedX) && close(y, expectedY) && close(r, expectedR);
}

function simplifySqrt(n) {
  if (!Number.isInteger(n) || n < 0) return `\\sqrt{${formatMixedFractionLatex(n)}}`;

  let outside = 1;
  let inside = n;

  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      outside *= i;
      inside /= i * i;
    }
  }

  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

export function renderAnswer(task) {
  const { center, rSquared } = task.data;

  const a = center.x;
  const b = center.y;

  const aLatex = formatMixedFractionLatex(a);
  const bLatex = formatMixedFractionLatex(b);
  const rSimplified = simplifySqrt(rSquared);

  return (
    <div className="space-y-2">
      <div>
        <strong>Środek okręgu:</strong>{" "}
        <InlineMath math={`S = (${aLatex}, ${bLatex})`} />
      </div>
      <div>
        <strong>Promień:</strong>{" "}
        <InlineMath math={`r = ${rSimplified}`} />
      </div>
    </div>
  );
}

export function renderExplanation(input) {
  const data = input?.data || input;

  const { Dx, Ey, F, a, b, rSquared } = data;

  const a2 = formatMixedFractionLatex(a ** 2);
  const b2 = formatMixedFractionLatex(b ** 2);
  const c = a ** 2 + b ** 2 - rSquared;
  const cLatex = formatMixedFractionLatex(c);
  const r2 = formatMixedFractionLatex(rSquared);
  const r = simplifySqrt(rSquared);

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Porównujemy równanie ogólne z postacią:
        <BlockMath
          math={`x^2 + y^2 ${Dx >= 0 ? "+ " + Dx : "- " + Math.abs(Dx)}x ${Ey >= 0 ? "+ " + Ey : "- " + Math.abs(Ey)}y ${F >= 0 ? "+ " + F : "- " + Math.abs(F)} = 0`}
        />
        <BlockMath math={`x^2 + y^2 - 2ax - 2by + c = 0`} />
        <p>Odczytujemy wartości <InlineMath math="a" /> i <InlineMath math="b" />:</p>
        <ul className="list-disc ml-6">
          <li>
            <BlockMath
              math={`-2a = ${Dx} \\Rightarrow a = \\frac{${-Dx}}{2} = ${formatMixedFractionLatex(a)}`}
            />
          </li>
          <li>
            <BlockMath
              math={`-2b = ${Ey} \\Rightarrow b = \\frac{${-Ey}}{2} = ${formatMixedFractionLatex(b)}`}
            />
          </li>
        </ul>
      </div>

      <div>
        <strong>Krok 2:</strong> Obliczamy promień:
        <p>Wiemy, że:</p>
        <BlockMath math={`r^2 = a^2 + b^2 - c = ${a2} + ${b2} - (${cLatex}) = ${r2}`} />
        <BlockMath math={`r = ${r}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Zatem:
        <div className="mt-2">
          <div><InlineMath math={`S = (${formatMixedFractionLatex(a)},\\ ${formatMixedFractionLatex(b)})`} /></div>
          <div><InlineMath math={`r = ${r}`} /></div>
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput) {
  if (
    !userInput ||
    Object.keys(userInput).length === 0 ||
    (
      userInput.x?.parsed === undefined &&
      userInput.y?.parsed === undefined &&
      userInput.radius?.parsed === undefined
    )
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const xRaw = userInput.x?.raw ?? "?";
  const yRaw = userInput.y?.raw ?? "?";
  const rRaw = userInput.radius?.raw ?? "?";

  // jeśli user wpisał ładne ułamki, pokazujemy podgląd latex
  const xLatex = typeof xRaw === "string" ? formatLatexPreview(xRaw, "x_S").split("=")[1] : xRaw;
  const yLatex = typeof yRaw === "string" ? formatLatexPreview(yRaw, "y_S").split("=")[1] : yRaw;

  return (
    <div className="space-y-2">
      <div>
        <InlineMath math={`S = (${xLatex}, ${yLatex})`} />
      </div>
      <div>
        <InlineMath math={`r = ${rRaw}`} />
      </div>
    </div>
  );
}
